import React from 'react'
import './inquiry-age-chart.css'

const SVG_WIDTH = 1040
const SVG_HEIGHT = 340
const MARGIN = { top: 32, right: 28, bottom: 58, left: 58 }
const ADULT_AGE = 18

const formatAge = (age) => (
  Number.isFinite(age) ? age.toFixed(1) : '—'
)

const InquiryAgeChart = ({ data }) => {
  const [tooltip, setTooltip] = React.useState(null)
  const months = Array.isArray(data?.months) ? data.months : []
  const plottedMonths = months.filter((month) => Number.isFinite(month?.averageAge))
  const summary = data?.summary || {}

  if (!plottedMonths.length) {
    return (
      <div className='inquiry-age-chart__empty'>
        No valid student birthdays were found for inquiries in the past five years.
      </div>
    )
  }

  const highestAverage = plottedMonths.reduce(
    (highest, month) => Math.max(highest, month.averageAge),
    0
  )
  const yMax = Math.max(20, Math.ceil((highestAverage + 2) / 5) * 5)
  const innerWidth = SVG_WIDTH - MARGIN.left - MARGIN.right
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom
  const xStep = innerWidth / Math.max(1, months.length - 1)
  const getX = (index) => MARGIN.left + (index * xStep)
  const getY = (value) => MARGIN.top + innerHeight - ((value / yMax) * innerHeight)

  let linePath = ''
  let previousPointWasValid = false
  months.forEach((month, index) => {
    if (!Number.isFinite(month.averageAge)) {
      previousPointWasValid = false
      return
    }
    linePath += `${previousPointWasValid ? ' L' : ' M'} ${getX(index)} ${getY(month.averageAge)}`
    previousPointWasValid = true
  })

  const tickValues = Array.from(new Set(
    [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(yMax * ratio))
  ))
  const adultY = getY(ADULT_AGE)
  const latest = summary.latestMonth

  return (
    <div className='inquiry-age-chart'>
      <div className='inquiry-age-chart__summary'>
        <div className='inquiry-age-chart__summary-card'>
          <span>Latest monthly average</span>
          <strong>{latest ? `${formatAge(latest.averageAge)} years` : '—'}</strong>
          <small>{latest ? `${latest.label} • ${latest.sampleSize} student${latest.sampleSize === 1 ? '' : 's'}` : 'No recent data'}</small>
        </div>
        <div className='inquiry-age-chart__summary-card'>
          <span>Five-year average</span>
          <strong>{Number.isFinite(summary.averageAge) ? `${formatAge(summary.averageAge)} years` : '—'}</strong>
          <small>{Number(summary.includedStudentCount || 0).toLocaleString()} valid student ages</small>
        </div>
        <div className='inquiry-age-chart__summary-card'>
          <span>Adults in latest month</span>
          <strong>{latest && Number.isFinite(latest.adultPercentage) ? `${latest.adultPercentage}%` : '—'}</strong>
          <small>Students age 18 or older</small>
        </div>
      </div>

      <div className='inquiry-age-chart__legend'>
        <span><i className='inquiry-age-chart__legend-dot' />Average age at inquiry</span>
        <span><i className='inquiry-age-chart__legend-line' />Adult threshold (18)</span>
      </div>

      <div className='inquiry-age-chart__plot-wrap'>
        <svg
          width={SVG_WIDTH}
          height={SVG_HEIGHT}
          role='img'
          aria-label='Average student age at inquiry by month for the past five years'
          onMouseLeave={() => setTooltip(null)}
        >
          {tickValues.map((value) => {
            const y = getY(value)
            return (
              <g key={value}>
                <line
                  x1={MARGIN.left}
                  x2={SVG_WIDTH - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke='rgba(148, 163, 184, 0.23)'
                />
                <text
                  x={MARGIN.left - 10}
                  y={y}
                  textAnchor='end'
                  dominantBaseline='middle'
                  fill='#94A3B8'
                >
                  {value}
                </text>
              </g>
            )
          })}

          {ADULT_AGE <= yMax && (
            <g>
              <line
                x1={MARGIN.left}
                x2={SVG_WIDTH - MARGIN.right}
                y1={adultY}
                y2={adultY}
                stroke='#F97316'
                strokeWidth='1.5'
                strokeDasharray='6 5'
                opacity='0.72'
              />
              <text
                x={SVG_WIDTH - MARGIN.right}
                y={adultY - 7}
                textAnchor='end'
                fill='#C2410C'
                fontWeight='600'
              >
                Age 18
              </text>
            </g>
          )}

          {months.map((month, index) => {
            const shouldLabel = index === 0 || index === months.length - 1 || index % 6 === 0
            if (!shouldLabel) return null
            return (
              <text
                key={month.key}
                x={getX(index)}
                y={SVG_HEIGHT - MARGIN.bottom + 25}
                textAnchor={index === 0 ? 'start' : index === months.length - 1 ? 'end' : 'middle'}
                fill='#64748B'
                fontSize='11'
                fontWeight='600'
              >
                {month.label}
              </text>
            )
          })}

          <path
            d={linePath.trim()}
            fill='none'
            stroke='#4F46E5'
            strokeWidth='3'
            strokeLinejoin='round'
            strokeLinecap='round'
          />

          {months.map((month, index) => {
            if (!Number.isFinite(month.averageAge)) return null
            const x = getX(index)
            const y = getY(month.averageAge)
            const tooltipData = { ...month, x, y }
            return (
              <g key={month.key}>
                <circle
                  cx={x}
                  cy={y}
                  r='10'
                  fill='transparent'
                  tabIndex='0'
                  aria-label={`${month.label}: average age ${formatAge(month.averageAge)} years from ${month.sampleSize} students`}
                  onMouseEnter={() => setTooltip(tooltipData)}
                  onMouseMove={() => setTooltip(tooltipData)}
                  onFocus={() => setTooltip(tooltipData)}
                  onBlur={() => setTooltip(null)}
                />
                <circle
                  cx={x}
                  cy={y}
                  r='3.7'
                  fill='#FFFFFF'
                  stroke='#4F46E5'
                  strokeWidth='2.5'
                  pointerEvents='none'
                />
              </g>
            )
          })}
        </svg>

        {tooltip && (
          <div
            className='inquiry-age-chart__tooltip'
            style={{
              left: `${Math.min(Math.max(tooltip.x, 82), SVG_WIDTH - 82)}px`,
              top: `${Math.max(tooltip.y - 10, 26)}px`,
            }}
          >
            <strong>{tooltip.label}</strong>
            <span>Average age: {formatAge(tooltip.averageAge)} years</span>
            <span>{tooltip.sampleSize} valid student{tooltip.sampleSize === 1 ? '' : 's'}</span>
            <span>{tooltip.adultPercentage}% were age 18+</span>
          </div>
        )}
      </div>

      <div className='inquiry-age-chart__footnote'>
        Age is calculated on the inquiry date. Missing or invalid birthdays and ages outside
        0–{summary.maxAge || 120} are excluded.
        {summary.excludedOutlierCount > 0 && ` ${summary.excludedOutlierCount} impossible age${summary.excludedOutlierCount === 1 ? ' was' : 's were'} removed.`}
        {summary.missingDobCount > 0 && ` ${summary.missingDobCount} student${summary.missingDobCount === 1 ? ' had' : 's had'} no usable birthday.`}
      </div>
    </div>
  )
}

export default InquiryAgeChart
